if (!customElements.get('product-builder')) {
  class ProductBuilder extends HTMLElement {
    connectedCallback() {
      if (this.config) {
        queueMicrotask(() => { if (this.isConnected) this.connectProductPrice(); });
        this.connectCart();
        return;
      }
      this.config = JSON.parse(this.dataset.config);
      this.products = new Map(this.config.products.map((product) => [String(product.id), product]));
      this.selection = new Map();
      this.money = new Intl.NumberFormat(this.config.locale || 'en', { style: 'currency', currency: this.config.currency || 'USD' });
      this.mainVariants = new Map((this.config.mainVariants || []).map((variant) => [String(variant.id), variant]));
      this.mainVariantChange = (event) => {
        if (event.detail?.form !== this.productForm) return;
        this.mainVariantId = event.detail.variant ? String(event.detail.variant.id) : null;
        this.nativeUnitPriceHidden = this.productSection.querySelector('.product-info__price .unit-price')?.hidden ?? true;
        this.updateProductPrice();
      };
      this.mainQuantityChange = (event) => {
        if (event.target.name === 'quantity' && event.target.form === this.productForm) this.updateProductPrice();
      };
      this.cartChange = (event) => {
        const cart = event.detail?.cart || event.detail?.model;
        if (cart?.items) this.syncCart(cart);
        else this.refreshCart();
      };
      this.cartPageShow = () => this.refreshCart();
      this.syncCart({ items: this.config.cartItems || [] });
      this.connectCart();
      queueMicrotask(() => { if (this.isConnected) this.connectProductPrice(); });
      this.addEventListener('change', (event) => this.handleChange(event));
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-builder-quantity-change]');
        if (!button || button.disabled) return;
        const input = button.closest('[data-builder-product]').querySelector('[data-builder-quantity]');
        input.value = Math.max(0, Math.trunc(Number(input.value) || 0) + Number(button.dataset.builderQuantityChange));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    disconnectedCallback() {
      this.productSection?.removeEventListener('on:variant:change', this.mainVariantChange);
      this.productSection?.removeEventListener('change', this.mainQuantityChange);
      ['on:cart:add', 'on:line-item:change', 'cart:updated', 'on:builder-cart:updated'].forEach((name) => {
        document.removeEventListener(name, this.cartChange);
      });
      window.removeEventListener('pageshow', this.cartPageShow);
      this.cartRequest = (this.cartRequest || 0) + 1;
    }

    connectCart() {
      ['on:cart:add', 'on:line-item:change', 'cart:updated', 'on:builder-cart:updated'].forEach((name) => {
        document.addEventListener(name, this.cartChange);
      });
      window.addEventListener('pageshow', this.cartPageShow);
      this.refreshCart();
    }

    async refreshCart() {
      const request = this.cartRequest = (this.cartRequest || 0) + 1;
      try {
        const response = await fetch(`${theme.routes.cart}.js`, { cache: 'no-store' });
        if (!response.ok) return;
        const cart = await response.json();
        if (this.isConnected && request === this.cartRequest) this.syncCart(cart);
      } catch (error) {
        // Keep the last known cart; submission always checks it again.
      }
    }

    syncCart(cart) {
      this.cartRequest = (this.cartRequest || 0) + 1;
      const items = cart.items.filter((item) => Number(item.quantity) > 0);
      this.cartItems = items;
      this.mainInCart = items.some((item) => String(item.product_id) === String(this.config.requirement.machineProductId));
      const status = this.querySelector('[data-builder-cart-status]');
      status.hidden = true;
      status.textContent = '';

      this.querySelectorAll('[data-builder-product]').forEach((row) => {
        const variants = new Map();
        items.filter((item) => String(item.product_id) === row.dataset.builderProduct).forEach((item) => {
          const id = String(item.variant_id);
          const entry = variants.get(id) || { quantity: 0, title: item.variant_title };
          entry.quantity += Number(item.quantity);
          variants.set(id, entry);
        });
        const note = row.querySelector('[data-builder-in-cart]');
        const text = Array.from(variants.values(), (variant) => {
          const template = variant.title && variant.title !== 'Default Title'
            ? this.config.labels.alreadyInCart : this.config.labels.alreadyInCartDefault;
          return template.replace('[quantity]', variant.quantity).replace('[variant]', variant.title || '');
        }).join('\n');
        if (note.textContent !== text) note.textContent = text;
        note.hidden = !text;
      });
      this.updateProductPrice();
    }

    connectProductPrice() {
      this.productForm = document.getElementById(this.dataset.productFormId);
      this.productSection = this.closest('.js-product');
      if (!this.productForm || !this.productSection) return;
      this.mainVariantId = this.productForm.elements.namedItem('id')?.value;
      this.nativeUnitPriceHidden = this.productSection.querySelector('.product-info__price .unit-price')?.hidden ?? true;
      this.productSection.addEventListener('on:variant:change', this.mainVariantChange);
      this.productSection.addEventListener('change', this.mainQuantityChange);
      this.updateProductPrice();
    }

    updateProductPrice() {
      this.updateLeadTime();
      const main = this.mainVariants.get(String(this.mainVariantId));
      const price = this.productSection?.querySelector('.product-info__price > .price');
      const current = price?.querySelector('.price__default .price__current');
      if (!main || !current) return;
      const rawQuantity = Number(this.productForm.elements.namedItem('quantity')?.value || 1);
      const quantity = Number.isSafeInteger(rawQuantity) && rawQuantity > 0 ? rawQuantity : 1;
      const supplies = this.getSelections().reduce((total, selection) => {
        const variant = this.products.get(selection.productId)?.variants.find((item) => String(item.id) === selection.variantId);
        return total + (variant?.price || 0) * selection.quantity;
      }, 0);
      const total = main.price * quantity + supplies;
      const compare = Math.max(main.price, main.compareAtPrice || 0) * quantity + supplies;
      const original = this.selection.size === 0 && quantity === 1;
      if (original) current.innerHTML = main.formattedPrice;
      else current.textContent = this.money.format(total / 100);
      const was = price.querySelector('.price__was');
      if (was) {
        if (original) was.innerHTML = main.formattedCompareAtPrice || '';
        else was.textContent = compare > total ? this.money.format(compare / 100) : '';
      }
      price.classList.toggle('price--on-sale', compare > total);
      const unit = price.querySelector('.unit-price');
      if (unit) unit.hidden = original ? this.nativeUnitPriceHidden : true;
      let cartNote = price.querySelector('[data-builder-price-cart-note]');
      if (this.mainInCart) {
        if (!cartNote) {
          cartNote = document.createElement('span');
          cartNote.className = 'product-builder__price-cart-note';
          cartNote.dataset.builderPriceCartNote = '';
          price.querySelector('.price__default').append(cartNote);
        }
        cartNote.textContent = `(${this.config.labels.inCart})`;
      } else {
        cartNote?.remove();
      }
    }

    getLeadTime(mainVariantId = this.mainVariantId, selections = this.getSelections()) {
      if (!this.config.leadTime?.enabled) return '';
      return window.ProductLeadTime.builder(this.config, mainVariantId, selections, this.cartItems || []);
    }

    getSupplyLeadTime({ productId, variantId }) {
      const product = this.products.get(String(productId));
      if (!product?.leadTimeEnabled) return '';
      const variant = product.variants.find((item) => String(item.id) === String(variantId));
      return variant ? window.ProductLeadTime.maximum(variant.leadTime) : '';
    }

    updateLeadTime() {
      if (!this.productForm || !this.productSection || !this.config.leadTime?.enabled) return;
      const text = this.getLeadTime();
      const title = this.config.leadTime.title;
      this.productSection.querySelectorAll(`[js-lead-time-product="${this.dataset.mainProductId}"]`).forEach((element) => {
        if (element.getAttribute('js-lead-time-type') === 'html') {
          const heading = document.createElement('strong');
          heading.textContent = `${title}:`;
          element.replaceChildren(heading, document.createTextNode(` ${text}`));
        } else {
          element.value = text;
          if (element.getAttribute('js-lead-time-type') === 'input-property') element.name = `properties[${title}]`;
        }
      });
    }

    getRequirement() {
      return this.config.requirement;
    }

    getSelections() {
      return Array.from(this.selection, ([productId, selection]) => ({ productId, ...selection }));
    }

    
handleChange(event) {
  const row = event.target.closest('[data-builder-product]');
  if (!row) return;

  const productId = row.dataset.builderProduct;
  const product = this.products.get(productId);
  if (!product) return;

  const input = row.querySelector('[data-builder-quantity]');
  const isVariantChange = event.target.matches('[data-builder-variant]');

  const rawQuantity = Number(input.value);
  let quantity =
    Number.isSafeInteger(rawQuantity) && rawQuantity > 0
      ? rawQuantity
      : 0;

  // Choosing a pack size automatically selects one pack.
  if (isVariantChange && quantity === 0) {
    quantity = 1;
    input.value = 1;
  }

  const picker = row.querySelector('[data-builder-variant]:checked');
  const variantId = picker?.value || row.dataset.builderVariantId;

  const variant = product.variants.find(
    (item) =>
      String(item.id) === String(variantId) &&
      item.available
  );

  // Store only valid, available selections.
  if (quantity > 0 && variant) {
    this.selection.set(productId, {
      variantId: String(variant.id),
      quantity
    });
  } else {
    this.selection.delete(productId);
  }

  this.querySelectorAll('[data-builder-product]').forEach((relatedRow) => {
    if (relatedRow.dataset.builderProduct !== productId) return;

    const selected = this.selection.get(productId);
    const selectedId = selected?.variantId;
    const count = selected?.quantity || 0;

    const current =
      product.variants.find(
        (item) => String(item.id) === String(selectedId)
      ) ||
      product.variants.find(
        (item) =>
          String(item.id) ===
            String(relatedRow.dataset.builderVariantId) &&
          item.available
      ) ||
      product.variants.find((item) => item.available);

    relatedRow.classList.toggle('is-selected', !!selectedId);

    const quantityInput =
      relatedRow.querySelector('[data-builder-quantity]');

    quantityInput.value = count;

    relatedRow.querySelector(
      '[data-builder-quantity-change="-1"]'
    ).disabled = count === 0;

    // Keep pack options enabled even at quantity 0.
    relatedRow.querySelectorAll('[data-builder-variant]').forEach(
      (relatedPicker) => {
        const option = product.variants.find(
          (item) => String(item.id) === relatedPicker.value
        );

        relatedPicker.disabled = !option?.available;
        relatedPicker.checked =
  count > 0 &&
  relatedPicker.value === String(current?.id);
      }
    );

    if (current) {
      relatedRow.dataset.builderVariantId = String(current.id);

      const priceElement = relatedRow.querySelector('[data-builder-price]');

if (count === 0 && product.variants.length > 1) {
  const minPrice = Math.min(
    ...product.variants
      .filter((variant) => variant.available)
      .map((variant) => variant.price)
  );

  priceElement.textContent =
    'From ' + this.money.format(minPrice / 100);
} else {
  priceElement.textContent = this.money.format(
    current.price * (count || 1) / 100
  );
}
    }
  });

  this.querySelectorAll('[data-builder-step-error]').forEach(
    (error) => {
      error.hidden = true;
    }
  );

  this.updateProductPrice();

  this.dispatchEvent(
    new CustomEvent('on:builder:change', {
      bubbles: true
    })
  );
}


    showMissingSteps(missing) {
      let firstStep;
      this.querySelectorAll('[data-builder-step]').forEach((step) => {
        const invalid = missing.some((item) => String(item.stepId) === step.dataset.builderStep);
        const error = step.querySelector('[data-builder-step-error]');
        error.hidden = !invalid;
        if (!invalid) return;
        const title = this.config.requirement.steps.find((item) => String(item.id) === step.dataset.builderStep).title;
        error.textContent = this.dataset.requiredMessage.replace('[step]', title);
        if (!firstStep) firstStep = step;
      });
      if (firstStep) {
        (firstStep.querySelector('[data-builder-quantity-change="1"]:not(:disabled)') || firstStep).focus({ preventScroll: true });
        firstStep.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      }
    }
  }
  customElements.define('product-builder', ProductBuilder);
}
